if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.galleryControlsOptions = { preserveScroll: false };

        this.initializeProductGalleryControls();

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        this.observeActiveThumbnailChanges();
        this.syncProductGalleryThumbnailButtons();
        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      initializeProductGalleryControls(options = {}) {
        this.galleryControlsOptions = {
          ...this.galleryControlsOptions,
          ...options,
        };

        if (!this.elements.viewer) return;

        if (this.dataset.productGalleryControlsReady === 'true') {
          this.normalizeThumbnailItems();
          this.syncActiveThumbnailFromMedia();
          this.updateProductGalleryThumbnailButtons();
          return;
        }

        this.normalizeThumbnailItems();

        this.getPreviousButtons().forEach((button) => {
          button.addEventListener(
            'click',
            (event) => {
              event.preventDefault();
              event.stopImmediatePropagation();
              this.switchActiveMediaByOffset(-1);
              button.blur();
            },
            true
          );
        });

        this.getNextButtons().forEach((button) => {
          button.addEventListener(
            'click',
            (event) => {
              event.preventDefault();
              event.stopImmediatePropagation();
              this.switchActiveMediaByOffset(1);
              button.blur();
            },
            true
          );
        });

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('.thumbnail').forEach((button) => {
            button.addEventListener(
              'click',
              (event) => {
                event.preventDefault();
                event.stopImmediatePropagation();
                const item = button.closest('.thumbnail-list__item[data-target]');
                const index = this.getThumbnailItems().findIndex((candidate) => candidate === item);
                if (index >= 0) this.switchActiveMediaToIndex(index);
                button.blur();
              },
              true
            );
          });

          const thumbnailSlider = this.getThumbnailSlider();
          if (thumbnailSlider) {
            thumbnailSlider.addEventListener('scroll', () => {
              window.requestAnimationFrame(() => this.updateProductGalleryThumbnailButtons());
            });
          }
        }

        this.elements.viewer.addEventListener('slideChanged', () => {
          window.requestAnimationFrame(() => this.syncActiveThumbnailFromMedia());
        });

        this.syncActiveThumbnailFromMedia();
        this.updateProductGalleryThumbnailButtons();
        this.dataset.productGalleryControlsReady = 'true';
      }

      onSlideChanged(event) {
        const mediaId = event.detail.currentElement?.dataset.mediaId;
        if (!mediaId) return;

        this.setActiveMediaClass(mediaId);

        const thumbnail = this.getThumbnailForMedia(event.detail.currentElement);
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }

        const activeMediaId = activeMedia.dataset.mediaId;
        this.setActiveMediaClass(activeMediaId);

        if (prepend) {
          activeMedia.parentElement.firstElementChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${activeMediaId}"]`);
            this.normalizeThumbnailItem(activeThumbnail);
            activeThumbnail?.parentElement.firstElementChild !== activeThumbnail &&
              activeThumbnail?.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: 'smooth' });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view.
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) {
          this.updateProductGalleryThumbnailButtons();
          return;
        }

        const activeThumbnail = this.getThumbnailForMedia(activeMedia);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveMediaClass(mediaId) {
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.toggle('is-active', element.dataset.mediaId === mediaId);
        });
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        this.elements.thumbnails
          .querySelectorAll('.thumbnail-list__item')
          .forEach((element) => element.classList.remove('is-active'));
        thumbnail.classList.add('is-active');
        thumbnail.querySelector('button').setAttribute('aria-current', 'true');

        this.scrollThumbnailIntoView(thumbnail);
        this.updateProductGalleryThumbnailButtons(thumbnail);
      }

      observeActiveThumbnailChanges() {
        this.thumbnailObserver = new MutationObserver((mutations) => {
          const activeButtonMutation = mutations.find(
            (mutation) =>
              mutation.type === 'attributes' &&
              mutation.attributeName === 'aria-current' &&
              mutation.target.getAttribute('aria-current') === 'true'
          );
          if (!activeButtonMutation) return;

          const thumbnail = activeButtonMutation.target.closest('.thumbnail-list__item');
          this.scrollThumbnailIntoView(thumbnail);
          this.updateProductGalleryThumbnailButtons(thumbnail);
        });

        this.thumbnailObserver.observe(this.elements.thumbnails, {
          subtree: true,
          attributes: true,
          attributeFilter: ['aria-current'],
        });
      }

      syncProductGalleryThumbnailButtons() {
        if (!this.elements.thumbnails.classList.contains('product-gallery-thumbnails')) return;

        const slider = this.getThumbnailSlider();
        if (!slider) return;

        slider.addEventListener('scroll', () => {
          window.requestAnimationFrame(() => this.updateProductGalleryThumbnailButtons());
        });
        this.updateProductGalleryThumbnailButtons();
      }

      updateProductGalleryThumbnailButtons(activeThumbnail = null) {
        const items = this.getThumbnailItems();
        const activeIndex = this.getActiveIndex(items, activeThumbnail);
        const isAtStart = activeIndex <= 0;
        const isAtEnd = !items.length || activeIndex >= items.length - 1;

        this.getPreviousButtons().forEach((button) => button.toggleAttribute('disabled', isAtStart));
        this.getNextButtons().forEach((button) => button.toggleAttribute('disabled', isAtEnd));
      }

      syncActiveThumbnailFromMedia() {
        if (!this.elements.thumbnails) {
          this.updateProductGalleryThumbnailButtons();
          return;
        }

        const activeMedia = this.elements.viewer.querySelector('.product__media-item.is-active[data-media-id]');
        const currentMedia =
          activeMedia ||
          this.elements.viewer.sliderItemsToShow?.[this.elements.viewer.currentPage - 1] ||
          this.elements.viewer.querySelector('[data-media-id]');
        const thumbnail = currentMedia
          ? this.getThumbnailForMedia(currentMedia)
          : this.elements.thumbnails.querySelector('.thumbnail[aria-current]')?.closest('.thumbnail-list__item[data-target]');

        if (currentMedia?.dataset.mediaId) this.setActiveMediaClass(currentMedia.dataset.mediaId);
        if (thumbnail) this.setActiveThumbnail(thumbnail);
        else this.updateProductGalleryThumbnailButtons();
      }

      getThumbnailForMedia(media) {
        if (!this.elements.thumbnails || !media) return null;

        const mediaKey = this.getImageKey(media);
        if (mediaKey) {
          const visualMatch = this.getThumbnailItems().find((item) => this.getImageKey(item) === mediaKey);
          if (visualMatch) return visualMatch;
        }

        const mediaId = media.dataset.mediaId;
        return mediaId ? this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`) : null;
      }

      getImageKey(element) {
        const image = element?.matches?.('img') ? element : element?.querySelector?.('img');
        if (!image) return '';

        const src =
          image.currentSrc ||
          image.src ||
          image.getAttribute('src') ||
          image.getAttribute('data-src') ||
          this.getFirstSrcsetUrl(image.getAttribute('srcset') || image.getAttribute('data-srcset'));

        return this.normalizeImageSource(src);
      }

      getFirstSrcsetUrl(srcset) {
        if (!srcset) return '';

        return srcset
          .split(',')
          .map((candidate) => candidate.trim().split(/\s+/)[0])
          .find(Boolean);
      }

      normalizeImageSource(src) {
        if (!src) return '';

        try {
          const url = new URL(src, window.location.href);
          const filename = decodeURIComponent(url.pathname.split('/').pop() || '');
          return filename.replace(/_(\d+x\d+|\d+x|x\d+|pico|icon|thumb|small|compact|medium|large|grande|master)(?=\.)/, '');
        } catch (error) {
          return src.split('?')[0];
        }
      }

      switchActiveMediaByOffset(offset) {
        const items = this.getThumbnailItems();
        if (!items.length) return;

        const nextIndex = Math.max(0, Math.min(items.length - 1, this.getActiveIndex(items) + offset));
        this.switchActiveMediaToIndex(nextIndex);
      }

      switchActiveMediaToIndex(index) {
        const items = this.getThumbnailItems();
        const targetItem = items[index];
        if (!targetItem) return;

        const scrollContainer = this.getPreservedScrollContainer();
        const previousScrollTop = scrollContainer?.scrollTop;

        this.setActiveMedia(targetItem.dataset.target, false);
        this.updateProductGalleryThumbnailButtons(targetItem);

        if (scrollContainer && typeof previousScrollTop === 'number') {
          scrollContainer.scrollTop = previousScrollTop;
          window.requestAnimationFrame(() => {
            scrollContainer.scrollTop = previousScrollTop;
          });
          window.setTimeout(() => {
            scrollContainer.scrollTop = previousScrollTop;
          }, 250);
        }
      }

      getActiveIndex(items, activeThumbnail = null) {
        if (!items.length) return 0;

        const activeItem =
          activeThumbnail ||
          this.elements.thumbnails?.querySelector('.thumbnail[aria-current="true"]')?.closest('.thumbnail-list__item[data-target]');
        const activeMedia = this.elements.viewer.querySelector('.product__media-item.is-active[data-media-id]');
        const activeMediaId = activeItem?.dataset.target || activeMedia?.dataset.mediaId;
        const activeIndex = items.findIndex((item) => item.dataset.target === activeMediaId);

        return activeIndex >= 0 ? activeIndex : 0;
      }

      getThumbnailItems() {
        if (!this.elements.thumbnails) return [];

        return Array.from(this.elements.thumbnails.querySelectorAll('.thumbnail-list__item[data-target]')).filter((item) => {
          const style = window.getComputedStyle(item);
          return (
            style.display !== 'none' &&
            style.visibility !== 'hidden' &&
            !item.classList.contains('thumbnail-list_item--variant')
          );
        });
      }

      getThumbnailSlider() {
        return (
          this.elements.thumbnails &&
          (this.elements.thumbnails.slider || this.elements.thumbnails.querySelector('[id^="Slider-Thumbnails"]'))
        );
      }

      getPreviousButtons() {
        return [
          this.elements.thumbnails?.querySelector('.slider-button--prev'),
          this.elements.viewer?.querySelector(':scope > .button_left'),
        ].filter(Boolean);
      }

      getNextButtons() {
        return [
          this.elements.thumbnails?.querySelector('.slider-button--next'),
          this.elements.viewer?.querySelector(':scope > .button_right'),
        ].filter(Boolean);
      }

      normalizeThumbnailItems() {
        if (!this.elements.thumbnails) return;

        const firstItem = this.elements.thumbnails.querySelector('.thumbnail-list__item[data-target]');
        this.normalizeThumbnailItem(firstItem);
      }

      normalizeThumbnailItem(item) {
        if (!item) return;

        item.classList.remove('thumbnail-list_item--variant');
        item.style.pointerEvents = '';

        const button = item.querySelector('.thumbnail');
        if (!button) return;

        button.disabled = false;
        button.removeAttribute('disabled');
        button.removeAttribute('aria-disabled');
        button.removeAttribute('tabindex');
        button.style.pointerEvents = '';
      }

      getPreservedScrollContainer() {
        if (!this.galleryControlsOptions.preserveScroll) return null;

        const candidates = [
          this.closest('.product--medium'),
          this.closest('.quick-add-modal__content-info'),
          this.closest('.quick-add-modal__content'),
        ].filter(Boolean);

        return candidates.find((element) => element.scrollHeight > element.clientHeight) || candidates[0] || null;
      }

      scrollThumbnailIntoView(thumbnail) {
        const slider = this.getThumbnailSlider();
        if (!slider || !thumbnail) return;
        if (
          typeof this.elements.thumbnails.isSlideVisible === 'function' &&
          this.elements.thumbnails.isSlideVisible(thumbnail, 10)
        ) {
          return;
        }

        const targetLeft = Math.max(0, thumbnail.offsetLeft - (slider.clientWidth - thumbnail.clientWidth) / 2);
        slider.scrollTo({ left: targetLeft, behavior: 'smooth' });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !this.elements.liveRegion) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
